import React, { useState, useEffect, useCallback } from 'react';
import {
  Link2, Plus, Edit3, Trash2, Search, Save, X,
  AlertTriangle, Calendar, Eye, CheckCircle, XCircle, Clock
} from 'lucide-react';
import {
  createMyProviderRelationship, RELATIONSHIP_TYPES, RELATIONSHIP_STATUS,
  isRelationshipActive, getDaysUntilExpiry
} from '../../data/models/MyProviderRelationship';

const STORAGE_KEY = 'medfast_provider_relationships';

const ProviderRelationshipManagement = () => {
  const [relationships, setRelationships] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewingId, setViewingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    participantId: '', providerId: '', providerName: '',
    relationshipType: '', startDate: '', endDate: '', status: 'active',
    canViewBudget: true, canSubmitClaims: true, canViewPlan: true, canViewClinicalNotes: false,
    consentScope: { coreSupports: true, capacityBuilding: false, capital: false, dailyLife: false, specificLineItems: [] }
  });

  const load = useCallback(() => {
    try {
      const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      setRelationships(data);
    } catch { setRelationships([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveAll = (data) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    setRelationships(data);
  };

  const filtered = relationships.filter(r => {
    const matchSearch = `${r.providerName} ${r.participantId} ${r.providerId}`.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === 'all' || r.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const openCreate = () => {
    setEditingId(null);
    setFormData({
      participantId: '', providerId: '', providerName: '',
      relationshipType: '', startDate: '', endDate: '', status: 'active',
      canViewBudget: true, canSubmitClaims: true, canViewPlan: true, canViewClinicalNotes: false,
      consentScope: { coreSupports: true, capacityBuilding: false, capital: false, dailyLife: false, specificLineItems: [] }
    });
    setShowForm(true);
    setError(null);
  };

  const openEdit = (rel) => {
    setEditingId(rel.id);
    setFormData({
      participantId: rel.participantId || '',
      providerId: rel.providerId || '',
      providerName: rel.providerName || '',
      relationshipType: rel.relationshipType || '',
      startDate: rel.startDate || '',
      endDate: rel.endDate || '',
      status: rel.status || 'active',
      canViewBudget: rel.canViewBudget ?? true,
      canSubmitClaims: rel.canSubmitClaims ?? true,
      canViewPlan: rel.canViewPlan ?? true,
      canViewClinicalNotes: rel.canViewClinicalNotes ?? false,
      consentScope: rel.consentScope || { coreSupports: true, capacityBuilding: false, capital: false, dailyLife: false, specificLineItems: [] }
    });
    setShowForm(true);
    setError(null);
  };

  const handleSave = () => {
    if (!formData.providerName.trim()) {
      setError('Provider name is required');
      return;
    }
    if (!formData.relationshipType) {
      setError('Relationship type is required');
      return;
    }

    const now = new Date().toISOString();
    if (editingId) {
      const updated = relationships.map(r => r.id === editingId ? { ...r, ...formData, updatedAt: now } : r);
      saveAll(updated);
    } else {
      const newItem = createMyProviderRelationship({ ...formData, id: `PR-${Date.now()}` });
      saveAll([...relationships, newItem]);
    }
    setShowForm(false);
    setError(null);
  };

  const handleDelete = (id) => {
    saveAll(relationships.filter(r => r.id !== id));
    setDeleteConfirm(null);
  };

  const viewing = viewingId ? relationships.find(r => r.id === viewingId) : null;

  const statusBadge = (r) => {
    const active = isRelationshipActive(r);
    const days = getDaysUntilExpiry(r);
    if (r.status === 'revoked') return { class: 'bg-red-100 text-red-800', label: 'Revoked' };
    if (r.status === 'expired' || (days !== null && days <= 0)) return { class: 'bg-gray-100 text-gray-800', label: 'Expired' };
    if (r.status === 'pending') return { class: 'bg-yellow-100 text-yellow-800', label: 'Pending' };
    if (days !== null && days <= 14) return { class: 'bg-orange-100 text-orange-800', label: `Expires in ${days}d` };
    if (active) return { class: 'bg-green-100 text-green-800', label: 'Active' };
    return { class: 'bg-gray-100 text-gray-600', label: r.status };
  };

  const expiringCount = relationships.filter(r => {
    const d = getDaysUntilExpiry(r);
    return d !== null && d > 0 && d <= 30;
  }).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">Provider Relationships (NDIS)</h2>
          <p className="text-gray-600 text-sm">Manage My Provider, Plan Manager, and Support Coordinator relationships</p>
        </div>
        <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 font-medium">
          <Plus className="w-4 h-4" /> Add Relationship
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-violet-100 rounded-lg"><Link2 className="w-5 h-5 text-violet-600" /></div>
            <div><p className="text-sm text-gray-500">Total</p><p className="text-lg font-bold">{relationships.length}</p></div>
          </div>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg"><CheckCircle className="w-5 h-5 text-green-600" /></div>
            <div><p className="text-sm text-gray-500">Active</p><p className="text-lg font-bold text-green-600">{relationships.filter(r => isRelationshipActive(r)).length}</p></div>
          </div>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${expiringCount > 0 ? 'bg-orange-100' : 'bg-gray-100'}`}>
              <Clock className={`w-5 h-5 ${expiringCount > 0 ? 'text-orange-600' : 'text-gray-400'}`} />
            </div>
            <div><p className="text-sm text-gray-500">Expiring Soon</p><p className={`text-lg font-bold ${expiringCount > 0 ? 'text-orange-600' : ''}`}>{expiringCount}</p></div>
          </div>
        </div>
        <div className="bg-white border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg"><XCircle className="w-5 h-5 text-red-600" /></div>
            <div><p className="text-sm text-gray-500">Expired/Revoked</p><p className="text-lg font-bold text-red-600">{relationships.filter(r => r.status === 'expired' || r.status === 'revoked').length}</p></div>
          </div>
        </div>
      </div>

      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" placeholder="Search providers..." value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500">
          <option value="all">All Statuses</option>
          {Object.values(RELATIONSHIP_STATUS).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <Link2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No provider relationships found.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(r => {
            const badge = statusBadge(r);
            return (
              <div key={r.id} className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-violet-100 rounded-xl">
                      <Link2 className="w-6 h-6 text-violet-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">{r.providerName}</h3>
                      <p className="text-sm text-gray-600">{r.relationshipType || 'Unknown type'}</p>
                      <div className="flex gap-3 mt-1 text-xs text-gray-500">
                        <span>Participant: {r.participantId || '—'}</span>
                        {r.startDate && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{r.startDate}</span>}
                        {r.endDate && <span>to {r.endDate}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 text-xs rounded-full font-medium ${badge.class}`}>{badge.label}</span>
                    <button onClick={() => setViewingId(r.id)} className="p-1.5 hover:bg-gray-100 rounded-lg"><Eye className="w-4 h-4 text-gray-600" /></button>
                    <button onClick={() => openEdit(r)} className="p-1.5 hover:bg-violet-100 rounded-lg"><Edit3 className="w-4 h-4 text-violet-600" /></button>
                    <button onClick={() => setDeleteConfirm(r.id)} className="p-1.5 hover:bg-red-100 rounded-lg"><Trash2 className="w-4 h-4 text-red-600" /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail View */}
      {viewing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b bg-violet-50 flex justify-between items-center">
              <h3 className="text-lg font-bold text-violet-800">{viewing.providerName}</h3>
              <button onClick={() => setViewingId(null)} className="p-1 hover:bg-violet-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div><span className="font-medium text-gray-500">Type</span><p>{viewing.relationshipType}</p></div>
                <div><span className="font-medium text-gray-500">Status</span><p><span className={`px-2 py-0.5 rounded-full text-xs ${statusBadge(viewing).class}`}>{statusBadge(viewing).label}</span></p></div>
                <div><span className="font-medium text-gray-500">Provider ID</span><p>{viewing.providerId || '—'}</p></div>
                <div><span className="font-medium text-gray-500">Participant ID</span><p>{viewing.participantId || '—'}</p></div>
                <div><span className="font-medium text-gray-500">Start Date</span><p>{viewing.startDate || '—'}</p></div>
                <div><span className="font-medium text-gray-500">End Date</span><p>{viewing.endDate || '—'}</p></div>
              </div>
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-2">Access Permissions</h4>
                <div className="grid grid-cols-2 gap-2">
                  {[['canViewBudget', 'View Budget'], ['canSubmitClaims', 'Submit Claims'], ['canViewPlan', 'View Plan'], ['canViewClinicalNotes', 'Clinical Notes']].map(([k, label]) => (
                    <div key={k} className="flex items-center gap-2">
                      {viewing[k] ? <CheckCircle className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-gray-400" />}
                      <span className="text-gray-700">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
              {viewing.consentScope && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-2">Consent Scope</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {[['coreSupports', 'Core Supports'], ['capacityBuilding', 'Capacity Building'], ['capital', 'Capital'], ['dailyLife', 'Daily Life']].map(([k, label]) => (
                      <div key={k} className="flex items-center gap-2">
                        {viewing.consentScope[k] ? <CheckCircle className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-gray-400" />}
                        <span className="text-gray-700">{label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b bg-violet-600 text-white flex justify-between items-center rounded-t-2xl">
              <h2 className="text-xl font-bold">{editingId ? 'Edit Relationship' : 'Add Provider Relationship'}</h2>
              <button onClick={() => setShowForm(false)} className="p-1 hover:bg-white/20 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4" />{error}</div>}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Provider Name *</label>
                  <input type="text" value={formData.providerName} onChange={e => setFormData(p => ({ ...p, providerName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Relationship Type *</label>
                  <select value={formData.relationshipType} onChange={e => setFormData(p => ({ ...p, relationshipType: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500">
                    <option value="">Select type...</option>
                    {Object.values(RELATIONSHIP_TYPES).map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Provider ID</label>
                  <input type="text" value={formData.providerId} onChange={e => setFormData(p => ({ ...p, providerId: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Participant ID</label>
                  <input type="text" value={formData.participantId} onChange={e => setFormData(p => ({ ...p, participantId: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input type="date" value={formData.startDate} onChange={e => setFormData(p => ({ ...p, startDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input type="date" value={formData.endDate} onChange={e => setFormData(p => ({ ...p, endDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select value={formData.status} onChange={e => setFormData(p => ({ ...p, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500">
                    {Object.values(RELATIONSHIP_STATUS).map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Access Permissions</h4>
                <div className="grid grid-cols-2 gap-3">
                  {[['canViewBudget', 'View Budget'], ['canSubmitClaims', 'Submit Claims'], ['canViewPlan', 'View Plan'], ['canViewClinicalNotes', 'View Clinical Notes']].map(([k, label]) => (
                    <label key={k} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg cursor-pointer">
                      <input type="checkbox" checked={formData[k]}
                        onChange={() => setFormData(p => ({ ...p, [k]: !p[k] }))}
                        className="rounded border-gray-300 text-violet-600 focus:ring-violet-500" />
                      <span className="text-sm">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Consent Scope</h4>
                <div className="grid grid-cols-2 gap-3">
                  {[['coreSupports', 'Core Supports'], ['capacityBuilding', 'Capacity Building'], ['capital', 'Capital'], ['dailyLife', 'Daily Life']].map(([k, label]) => (
                    <label key={k} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg cursor-pointer">
                      <input type="checkbox" checked={formData.consentScope?.[k] ?? false}
                        onChange={() => setFormData(p => ({
                          ...p,
                          consentScope: { ...p.consentScope, [k]: !p.consentScope?.[k] }
                        }))}
                        className="rounded border-gray-300 text-violet-600 focus:ring-violet-500" />
                      <span className="text-sm">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-end gap-3 rounded-b-2xl">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100">Cancel</button>
              <button onClick={handleSave} className="px-6 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 flex items-center gap-2 font-medium"><Save className="w-4 h-4" />{editingId ? 'Update' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <h3 className="text-lg font-bold text-red-800 mb-2">Delete Relationship</h3>
            <p className="text-gray-600 mb-4">Remove the relationship with <strong>{relationships.find(r => r.id === deleteConfirm)?.providerName}</strong>?</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100">Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderRelationshipManagement;
